import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, Plus, Trash2, Database, ChevronRight, FileText, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * PitchTelemetryImporter
 * ──────────────────────
 * Attach FlightScope / TrackMan / Rapsodo / Yakkertech / 6-4-3 / Stalker /
 * K-Vest / PlayerData vendor data to an existing pitch_mechanics capture.
 *
 * Three input paths the coach can mix freely:
 *   1. Upload CSV  — first row = headers, rest = rows. POSTed as raw rows
 *                    array; backend's `parse_pitch()` does vendor-specific
 *                    column mapping.
 *   2. Paste JSON  — full vendor export or just an array.
 *   3. Manual row  — single-pitch entry (pitch type, velocity, spin, etc.)
 *                    sent with `source: "manual"` (skips the parser).
 *
 * Props:
 *   captureId  — `event_id` returned by the most-recent POST /screening/pitch-mechanics
 *   onAttached — (response) => void called after a successful attach
 */
const SOURCES = [
  { v: "flightscope", label: "FlightScope" },
  { v: "trackman",    label: "TrackMan" },
  { v: "rapsodo",     label: "Rapsodo" },
  { v: "yakkertech",  label: "Yakkertech" },
  { v: "643charts",   label: "6-4-3 Charts" },
  { v: "stalker",     label: "Stalker Pro" },
  { v: "kvest",       label: "K-Vest" },
  { v: "playerdata",  label: "PlayerData" },
];

const PITCH_TYPES = ["FB", "SI", "CT", "SL", "CB", "CH", "SP", "KN"];

export default function PitchTelemetryImporter({ captureId, onAttached }) {
  const [source, setSource] = useState("flightscope");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("manual"); // manual | csv | json
  const [jsonText, setJsonText] = useState("");
  const [manualRows, setManualRows] = useState([emptyManual()]);

  const canSubmit = useMemo(() => {
    if (mode === "manual") return manualRows.some((r) => r.velocity_mph);
    if (mode === "json") return jsonText.trim().length > 5;
    return false;
  }, [mode, manualRows, jsonText]);

  async function submit() {
    if (!captureId) {
      toast.error("Save a capture first, then attach telemetry.");
      return;
    }
    setBusy(true);
    try {
      let payload;
      if (mode === "manual") {
        const rows = manualRows
          .filter((r) => r.velocity_mph)
          .map((r) => ({
            velocity_mph: numeric(r.velocity_mph),
            spin_rpm: numeric(r.spin_rpm),
            pitch_type: r.pitch_type || null,
            induced_vert_break_in: numeric(r.ivb),
            horz_break_in: numeric(r.hb),
            extension_ft: numeric(r.extension),
            release_height_ft: numeric(r.release_h),
            release_side_ft: numeric(r.release_s),
            tilt_clock: r.tilt || null,
          }));
        payload = { source: "manual", data: rows };
      } else {
        let parsed;
        try {
          parsed = JSON.parse(jsonText);
        } catch (e) {
          toast.error("That doesn't look like valid JSON.");
          setBusy(false);
          return;
        }
        payload = { source, data: parsed };
      }
      const { data } = await api.post(`/screening/pitch-mechanics/${captureId}/telemetry`, payload);
      toast.success(`Attached ${data.summary?.pitches ?? 0} pitches from ${data.summary?.source ?? source}.`);
      onAttached?.(data);
      // Reset manual rows
      if (mode === "manual") setManualRows([emptyManual()]);
      if (mode === "json") setJsonText("");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not attach telemetry — check the payload format.");
    } finally {
      setBusy(false);
    }
  }

  async function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const text = await f.text();
      const rows = parseCsv(text);
      if (!rows.length) { toast.error("CSV is empty or unreadable."); setBusy(false); return; }
      const { data } = await api.post(`/screening/pitch-mechanics/${captureId}/telemetry`, {
        source, data: rows,
      });
      toast.success(`Attached ${data.summary?.pitches ?? 0} pitches from ${f.name}.`);
      onAttached?.(data);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not parse the CSV.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  }

  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-5" data-testid="pitch-telemetry-importer">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">Vendor Telemetry · Pitch-by-Pitch</div>
          <div className="text-base font-display font-black text-white mt-0.5">Fuse FlightScope / TrackMan / Rapsodo into this capture</div>
        </div>
        <Database className="h-5 w-5 text-white/40" />
      </div>

      {/* Vendor + mode tabs */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="telemetry-sources">
        {SOURCES.map((s) => (
          <button key={s.v} onClick={() => setSource(s.v)} data-testid={`telemetry-src-${s.v}`}
                  className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border
                              ${source === s.v
                                ? "border-[var(--brand-secondary)]/70 bg-[var(--brand-secondary)]/15 text-white"
                                : "border-white/15 bg-black/30 text-white/55 hover:text-white"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex gap-1.5 mb-4" data-testid="telemetry-modes">
        {[
          { v: "manual", label: "Manual entry" },
          { v: "csv",    label: "Upload CSV" },
          { v: "json",   label: "Paste JSON" },
        ].map((m) => (
          <button key={m.v} onClick={() => setMode(m.v)} data-testid={`telemetry-mode-${m.v}`}
                  className={`flex-1 text-xs font-bold uppercase tracking-wider rounded-lg px-3 py-2 border
                              ${mode === m.v
                                ? "border-[var(--brand-secondary)]/70 bg-[var(--brand-secondary)]/15 text-white"
                                : "border-white/15 bg-black/30 text-white/60 hover:text-white"}`}>
            {m.label}
          </button>
        ))}
      </div>

      {/* MANUAL */}
      {mode === "manual" && (
        <div className="space-y-2">
          {manualRows.map((r, idx) => (
            <div key={r._uid || idx} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`telemetry-manual-row-${idx}`}>
              <select value={r.pitch_type} onChange={(e) => setRow(setManualRows, idx, "pitch_type", e.target.value)}
                      className="col-span-2 h-9 rounded bg-black/30 border border-white/10 text-white text-xs px-2"
                      data-testid={`manual-type-${idx}`}>
                <option value="">Type</option>
                {PITCH_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <Input placeholder="velo" value={r.velocity_mph} onChange={(e) => setRow(setManualRows, idx, "velocity_mph", e.target.value)}
                     className="col-span-2 h-9 text-xs font-mono-num" data-testid={`manual-velo-${idx}`} />
              <Input placeholder="spin" value={r.spin_rpm} onChange={(e) => setRow(setManualRows, idx, "spin_rpm", e.target.value)}
                     className="col-span-2 h-9 text-xs font-mono-num" data-testid={`manual-spin-${idx}`} />
              <Input placeholder="IVB" value={r.ivb} onChange={(e) => setRow(setManualRows, idx, "ivb", e.target.value)}
                     className="col-span-1 h-9 text-xs font-mono-num" data-testid={`manual-ivb-${idx}`} />
              <Input placeholder="HB" value={r.hb} onChange={(e) => setRow(setManualRows, idx, "hb", e.target.value)}
                     className="col-span-1 h-9 text-xs font-mono-num" data-testid={`manual-hb-${idx}`} />
              <Input placeholder="ext" value={r.extension} onChange={(e) => setRow(setManualRows, idx, "extension", e.target.value)}
                     className="col-span-1 h-9 text-xs font-mono-num" data-testid={`manual-ext-${idx}`} />
              <Input placeholder="rel H" value={r.release_h} onChange={(e) => setRow(setManualRows, idx, "release_h", e.target.value)}
                     className="col-span-1 h-9 text-xs font-mono-num" data-testid={`manual-relh-${idx}`} />
              <Input placeholder="tilt" value={r.tilt} onChange={(e) => setRow(setManualRows, idx, "tilt", e.target.value)}
                     className="col-span-1 h-9 text-xs font-mono-num" data-testid={`manual-tilt-${idx}`} />
              <button onClick={() => setManualRows((rs) => {
                        const next = rs.filter((_, i) => i !== idx);
                        return next.length ? next : [emptyManual()];
                      })}
                      className="col-span-1 text-white/40 hover:text-red-400" data-testid={`manual-del-${idx}`}>
                <Trash2 className="h-4 w-4 mx-auto" />
              </button>
            </div>
          ))}
          <Button onClick={() => setManualRows((rs) => [...rs, emptyManual()])} variant="outline"
                  className="w-full mt-2 border-white/15 text-white/70" data-testid="manual-add-row">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add pitch
          </Button>
          <div className="text-[10px] text-white/40 mt-1">
            Velo (mph) · Spin (rpm) · IVB / HB (in) · Ext / Rel H (ft) · Tilt (e.g. 1:30)
          </div>
        </div>
      )}

      {/* CSV */}
      {mode === "csv" && (
        <label className="block rounded-lg border-2 border-dashed border-white/15 p-6 text-center cursor-pointer hover:border-[var(--brand-secondary)]/50 transition-colors"
               data-testid="telemetry-csv-drop">
          <Upload className="h-6 w-6 mx-auto text-white/40 mb-2" />
          <div className="text-sm font-bold text-white">Drop your {SOURCES.find(s => s.v === source)?.label} CSV here</div>
          <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">First row = headers · Each row = one pitch</div>
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
        </label>
      )}

      {/* JSON */}
      {mode === "json" && (
        <div>
          <textarea value={jsonText} onChange={(e) => setJsonText(e.target.value)}
                    placeholder='Paste raw vendor JSON (e.g. {"pitches":[...]})…'
                    className="w-full h-40 rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-xs text-white font-mono resize-none"
                    data-testid="telemetry-json-input" />
        </div>
      )}

      {mode !== "csv" && (
        <Button onClick={submit} disabled={!canSubmit || busy}
                className="w-full mt-3" style={{ backgroundColor: "var(--brand-secondary)" }}
                data-testid="telemetry-attach-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ChevronRight className="h-4 w-4 mr-2" />}
          Attach to capture
        </Button>
      )}
    </Card>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────
function emptyManual() {
  return {
    _uid: (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2),
    pitch_type: "", velocity_mph: "", spin_rpm: "", ivb: "", hb: "",
    extension: "", release_h: "", release_s: "", tilt: "",
  };
}
function setRow(setter, idx, key, val) {
  setter((rs) => rs.map((r, i) => i === idx ? { ...r, [key]: val } : r));
}
function numeric(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Very small CSV parser — first row is headers, the rest are rows. Quoted
 *  values supported. Vendor-specific column names are resolved server-side
 *  by `parse_pitch()`. */
function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length < 2) return [];
  const headers = splitCsvLine(lines[0]);
  return lines.slice(1).map((ln) => {
    const cells = splitCsvLine(ln);
    const row = {};
    headers.forEach((h, i) => { row[h.trim()] = cells[i] ?? ""; });
    return row;
  });
}
function splitCsvLine(line) {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') { inQ = false; }
      else cur += c;
    } else {
      if (c === ',') { out.push(cur); cur = ""; }
      else if (c === '"') inQ = true;
      else cur += c;
    }
  }
  out.push(cur);
  return out;
}
